import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = {
  key: 0,
  class: "absolute -top-[45px] left-1/2 -translate-x-1/2 flex gap-2 items-center whitespace-nowrap"
}
const _hoisted_3 = { class: "font-semibold" }
const _hoisted_4 = { key: 0 }

import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import { InformationCircleIcon as SolidInformationCircleIcon } from "@heroicons/vue/24/solid";
import {
  Connection,
  Edge,
  Node,
  NodeMouseEvent,
  useVueFlow,
  VueFlow,
  GraphEdge,
  GraphNode,
  EdgeChange,
} from "@vue-flow/core";
import "@vue-flow/core/dist/style.css";
import "@vue-flow/core/dist/theme-default.css";
import { format } from "date-fns";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import OaiTooltip from "shared/components/other/OaiTooltip.vue";
import {
  CriticalPathEdge,
  CriticalPathNode,
  CriticalPathPosition,
  CriticalPathContext,
  CriticalPathEx,
  CriticalPathLayout,
  CriticalPathDelta,
} from "shared/types/CriticalPath";
import { HierarchyTagStore } from "shared/types/HierarchyTag";
import { PlanConfig } from "shared/types/Plan";
import CriticalPathBackground from "shared/views/critical_path/components/CriticalPathBackground.vue";
import CriticalPathDelay from "shared/views/critical_path/components/CriticalPathDelay.vue";
import CriticalPathEdgeMarker from "shared/views/critical_path/components/CriticalPathEdgeMarker.vue";
import CriticalPathGraphNode from "shared/views/critical_path/components/CriticalPathGraphNode.vue";
import CriticalPathTagMenu from "shared/views/critical_path/components/CriticalPathTagMenu.vue";
import { getCriticalPathEdgeIds } from "shared/views/critical_path/criticalPath";
import {
  calculatePathDeltas,
  calculateDeltaByPaths,
} from "shared/views/critical_path/criticalPathDelta";
import { NodeHeight, NodeWidth } from "shared/views/critical_path/criticalPathLayout";
import { getLastPlannedPath } from "../criticalPathDelta";
import "./criticalPath.css";


export default /*#__PURE__*/_defineComponent({
  __name: 'CriticalPathGraph',
  props: {
    criticalPath: {},
    context: {},
    hierarchyTags: {},
    height: {},
    planConfig: {},
    selectionMode: {},
    layout: {},
    readonly: { type: Boolean },
    initialSelectedNodeId: {}
  },
  emits: ["change", "selectNodes"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();

const { nodes: vueFlowNodes } = useVueFlow();

const tagContextMenuPosition = ref<{ x: number; y: number } | null>(null);
const isEntireDeltaLocked = ref(false);
const isEntireDeltaHighlighted = ref(false);

const nodes = computed<Node<CriticalPathNode>[]>(() => {
  const nodePositionById = props.layout.nodes.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {} as Record<string, CriticalPathPosition<CriticalPathNode>>);
  return props.criticalPath.nodes
    .map((node) => {
      const nodePosition = nodePositionById[node._id];
      if (!nodePosition) {
        return null;
      }
      return {
        id: node._id,
        width: NodeWidth,
        height: NodeHeight,
        type: "criticalPath",
        data: node,
        deletable: false,
        position: { x: nodePosition.x, y: nodePosition.y },
      };
    })
    .filter((node) => node) as Node<CriticalPathNode>[];
});

const selectedNodeIds = computed(() =>
  vueFlowNodes.value.filter((node) => node.selected).map((node) => node.id),
);

const dimmed = computed(() => selectedNodeIds.value.length > 0);

const criticalPathEdgeAndNodeIds = computed(() => {
  const lastNodeId =
    isEntireDeltaHighlighted.value && entirePathDelta.value
      ? entirePathDelta.value.lastNodeId
      : null;
  return getCriticalPathEdgeIds(
    props.criticalPath,
    selectedNodeIds.value,
    lastNodeId,
    props.context,
  );
});

const edges = computed<Edge[]>(() => {
  const validNodeIds = new Set(nodes.value.map((node) => node.id));
  return props.criticalPath.edges
    .filter((edge) => validNodeIds.has(edge.source_id) && validNodeIds.has(edge.target_id))
    .map((edge) => {
      const id = `${edge.source_id}_${edge.target_id}`;
      const highlighted = criticalPathEdgeAndNodeIds.value.edgeIds.has(id);
      return {
        id,
        source: edge.source_id,
        target: edge.target_id,
        markerEnd: "oaiArrowClosed",
        updatable: false,
        class: !highlighted && dimmed.value ? "dimmed" : "",
        animated: highlighted,
      };
    });
});

const pathDeltas = computed(() => calculatePathDeltas(props.criticalPath, props.context));

const entirePathDelta = computed(() => calculateDeltaByPaths(pathDeltas.value, props.context));

const lastPlannedPath = computed(() => getLastPlannedPath(pathDeltas.value));

// even though one node can be part of more than one path,
// the delta for it will be the same for each path,
// because the delta depends on the preceding nodes
const deltasByNodeId = computed(() =>
  pathDeltas.value.reduce((acc, pathDelta) => {
    for (const delta of pathDelta.nodeDeltas) {
      if (delta.node) {
        acc[delta.node._id] = delta;
      }
    }
    return acc;
  }, {} as Record<string, CriticalPathDelta>),
);

const lastNodeIds = computed(
  () => new Set(pathDeltas.value.map((pathDelta) => pathDelta.lastNodeId)),
);

const lastFinishedSelectedNodeIds = computed(() => {
  const lastFinishedNodeIds = pathDeltas.value
    .filter((pathDelta) => criticalPathEdgeAndNodeIds.value.nodeIds.has(pathDelta.lastNodeId))
    .map((pathDelta) => pathDelta.lastFinishedNodeId)
    .filter((lastFinishedNodeId) => lastFinishedNodeId) as string[];
  return new Set<string>(lastFinishedNodeIds);
});

const levelIndexes = computed(() =>
  props.layout.levels.reduce((acc, level, index) => {
    acc[level.id] = index;
    return acc;
  }, {} as Record<string, number>),
);

const handleConnect = (params: Connection) => {
  const edgeIds = new Set(edges.value.map((edge) => `${edge.source}_${edge.target}`));
  if (edgeIds.has(`${params.source}_${params.target}`)) {
    return;
  }
  const newEdge: CriticalPathEdge = {
    source_id: params.source,
    target_id: params.target,
  };
  emit("change", {
    ...props.criticalPath,
    edges: [...props.criticalPath.edges, newEdge],
  });
};

const isValidConnection = (
  connection: Connection,
  elements: {
    edges: GraphEdge[];
    nodes: GraphNode[];
    sourceNode: GraphNode<CriticalPathNode>;
    targetNode: GraphNode<CriticalPathNode>;
  },
) => {
  const from = elements.sourceNode.data;
  const to = elements.targetNode.data;
  const fromLevelIndex = levelIndexes.value[from.level_id] || 0;
  const toLevelIndex = levelIndexes.value[to.level_id] || 0;
  return (
    (from.building_id === to.building_id || props.layout.sharedLevelIds.has(from.level_id)) &&
    toLevelIndex < fromLevelIndex
  );
};

const deleteKeyCodePredicate = (event: KeyboardEvent) =>
  !props.readonly && (event.key === "Backspace" || event.key === "Delete");

const handleEdgesChange = (changes: EdgeChange[]) => {
  const removedIds = changes.reduce((acc, change) => {
    if (change.type === "remove") {
      acc.add(`${change.source}_${change.target}`);
    }
    return acc;
  }, new Set<string>());

  if (removedIds.size > 0) {
    emit("change", {
      ...props.criticalPath,
      edges: props.criticalPath.edges.filter(
        (edge) => !removedIds.has(`${edge.source_id}_${edge.target_id}`),
      ),
    });
  }
};

const highlightEntireDelta = (options?: { lock: boolean }) => {
  const nodeId = entirePathDelta.value?.lastFinishedNodeId;
  vueFlowNodes.value.forEach((node) => {
    node.selected = node.id === nodeId;
  });
  nextTick(() => {
    isEntireDeltaHighlighted.value = true;
    isEntireDeltaLocked.value =
      options?.lock !== undefined ? options.lock : isEntireDeltaLocked.value;
  });
};

const handleEntireDeltaClick = () => {
  isEntireDeltaLocked.value = !isEntireDeltaLocked.value;
};

const handleEntireDeltaMouseEnter = () => {
  highlightEntireDelta();
};

const handleEntireDeltaMouseLeave = () => {
  if (isEntireDeltaLocked.value) {
    return;
  }
  vueFlowNodes.value.forEach((node) => {
    node.selected = false;
  });
};

const handleSelectionContextMenu = (payload: NodeMouseEvent) => {
  if (
    !payload.node.selected ||
    props.selectionMode !== "simple" ||
    props.criticalPath.tags.length === 0
  ) {
    return;
  }
  const event = payload.event as MouseEvent;
  event.preventDefault();
  tagContextMenuPosition.value = {
    x: event.pageX,
    y: event.pageY,
  };
};

watch(
  () => props.selectionMode,
  () => {
    vueFlowNodes.value.forEach((node) => {
      node.selected = false;
    });
  },
);

onMounted(() => {
  if (props.initialSelectedNodeId) {
    const node = vueFlowNodes.value.find(({ id }) => id === props.initialSelectedNodeId);
    if (node) {
      node.selected = true;
    }
  } else {
    highlightEntireDelta({ lock: true });
  }
});

watch(selectedNodeIds, () => {
  emit("selectNodes", selectedNodeIds.value);
  isEntireDeltaHighlighted.value = false;
  isEntireDeltaLocked.value = false;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(CriticalPathEdgeMarker, { class: "absolute" }),
    (entirePathDelta.value && entirePathDelta.value.delta.endDate)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", null, [
            _createTextVNode(_toDisplayString(entirePathDelta.value.delta.projectedEndDate
            ? _unref(t)("analytics.critical_path.projected_project_end")
            : _unref(t)("analytics.critical_path.actual_end")) + ": ", 1),
            _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(format)(entirePathDelta.value.delta.endDate, "dd.MM.yyyy")), 1)
          ]),
          (entirePathDelta.value)
            ? (_openBlock(), _createBlock(CriticalPathDelay, {
                key: 0,
                deltaForNode: entirePathDelta.value.delta,
                cls: "z-[100]"
              }, null, 8, ["deltaForNode"]))
            : _createCommentVNode("", true),
          (_ctx.selectionMode === 'criticalPath')
            ? (_openBlock(), _createBlock(OaiTooltip, {
                key: 1,
                position: "right"
              }, {
                tooltip: _withCtx(() => [
                  (!isEntireDeltaLocked.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_unref(t)("analytics.critical_path.click_to_lock")), 1))
                    : _createCommentVNode("", true)
                ]),
                default: _withCtx(() => [
                  (!isEntireDeltaLocked.value)
                    ? (_openBlock(), _createBlock(_unref(InformationCircleIcon), {
                        key: 0,
                        class: "w-6 h-6 text-gray-600 cursor-pointer",
                        onClick: handleEntireDeltaClick,
                        onMouseenter: handleEntireDeltaMouseEnter,
                        onMouseleave: handleEntireDeltaMouseLeave
                      }))
                    : (_openBlock(), _createBlock(_unref(SolidInformationCircleIcon), {
                        key: 1,
                        class: "w-6 h-6 text-gray-500 cursor-pointer",
                        onClick: handleEntireDeltaClick,
                        onMouseenter: handleEntireDeltaMouseEnter,
                        onMouseleave: handleEntireDeltaMouseLeave
                      }))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(VueFlow), {
      class: "oaiCriticalPathGraph",
      nodes: nodes.value,
      edges: edges.value,
      nodesDraggable: false,
      nodesFocusable: false,
      nodesConnectable: !_ctx.readonly,
      isValidConnection: isValidConnection,
      deleteKeyCode: deleteKeyCodePredicate,
      minZoom: 0.05,
      maxZoom: 4,
      fitViewOnInit: true,
      onConnect: handleConnect,
      onEdgesChange: handleEdgesChange,
      onNodeContextMenu: handleSelectionContextMenu
    }, {
      "node-criticalPath": _withCtx((props) => [
        _createVNode(CriticalPathGraphNode, {
          criticalPath: _ctx.criticalPath,
          criticalPathNode: props.data,
          dimmed: dimmed.value,
          selected: 
            props.selected ||
            (criticalPathEdgeAndNodeIds.value.nodeIds.has(props.id) && _ctx.selectionMode === 'criticalPath')
          ,
          highlightedYellow: 
            lastFinishedSelectedNodeIds.value.has(props.id) && _ctx.selectionMode === 'criticalPath'
          ,
          highlightedBlue: 
            selectedNodeIds.value.length > 0 &&
            isEntireDeltaHighlighted.value &&
            lastPlannedPath.value?.lastNodeId === props.id &&
            _ctx.selectionMode === 'criticalPath'
          ,
          highlightedPurple: 
            selectedNodeIds.value.length > 0 &&
            isEntireDeltaHighlighted.value &&
            entirePathDelta.value?.lastNodeId === props.id
          ,
          layout: _ctx.layout,
          context: _ctx.context,
          deltaForNode: deltasByNodeId.value[props.id],
          isLastNode: lastNodeIds.value.has(props.id)
        }, null, 8, ["criticalPath", "criticalPathNode", "dimmed", "selected", "highlightedYellow", "highlightedBlue", "highlightedPurple", "layout", "context", "deltaForNode", "isLastNode"])
      ]),
      default: _withCtx(() => [
        _createVNode(CriticalPathBackground, { layout: _ctx.layout }, null, 8, ["layout"])
      ]),
      _: 1
    }, 8, ["nodes", "edges", "nodesConnectable"]),
    (tagContextMenuPosition.value)
      ? (_openBlock(), _createBlock(CriticalPathTagMenu, {
          key: 1,
          position: tagContextMenuPosition.value,
          criticalPath: _ctx.criticalPath,
          selectedNodeIds: selectedNodeIds.value,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (tagContextMenuPosition.value = null)),
          onChange: _cache[1] || (_cache[1] = ($event: any) => (emit('change', $event)))
        }, null, 8, ["position", "criticalPath", "selectedNodeIds"]))
      : _createCommentVNode("", true)
  ]))
}
}

})